import { useState, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Card, CardContent } from "ui/card";
import { Button } from "ui/button";
import { InputFieldset } from "ui/inputFieldset";
import { SelectFieldset } from "ui/selectFieldset";
import { SelectTriggerUI } from "ui/selectTrigger";
import { MultiSelect } from "ui/multi-select";
import { Label } from "ui/label";
import { TextareaUI } from "ui/textareaUI";
import { Select, SelectContent, SelectItem, SelectValue } from "ui/select";
import { Ban, Building2 } from "lucide-react";
import { toast } from "sonner";
import {
  getProperties,
  PropertyApi,
  getPropertyById,
  PropertyDetailsApi,
  PropertyRoomTypeApi,
  getTourOperatorTypes,
  TourOperatorTypeApi,
  getRoomTypes,
  RoomTypeApi,
  getPropertyAvailabilityBlockById,
  updatePropertyAvailabilityBlock,
} from "services/propertyAvailabilityService";
import { FullScreenLoading } from "components/ui/loader";
import { usePageHeader } from "contexts/PageHeaderContext";

const BlockPropertyEdit = () => {
  const navigate = useNavigate();
  const { setPageHeader } = usePageHeader();
  const { publicId } = useParams();
  const today = new Date().toISOString().split("T")[0];

  const [properties, setProperties] = useState<PropertyApi[]>([]);
  const [tourOperatorTypes, setTourOperatorTypes] = useState<
    TourOperatorTypeApi[]
  >([]);
  const [selectedPropertyDetails, setSelectedPropertyDetails] =
    useState<PropertyDetailsApi | null>(null);
  const [propertyRoomTypes, setPropertyRoomTypes] = useState<
    PropertyRoomTypeApi[]
  >([]);
  const [roomTypes, setRoomTypes] = useState<RoomTypeApi[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingProperties, setLoadingProperties] = useState(false);
  const [loadingTourOperatorTypes, setLoadingTourOperatorTypes] =
    useState(false);
  const [loadingPropertyDetails, setLoadingPropertyDetails] = useState(false);

  const [blockFormData, setBlockFormData] = useState({
    propertyName: "",
    roomType: "",
    numberOfRooms: 1,
    availableTo: [] as string[],
    blockStartDate: "",
    blockEndDate: "",
    remark: "",
    status: "active" as "active" | "inactive",
  });

  const [errors, setErrors] = useState({
    propertyName: "",
    roomType: "",
    numberOfRooms: "",
    availableTo: "",
    blockStartDate: "",
    blockEndDate: "",
    remark: "",
  });

  useEffect(() => {
    setPageHeader({
      title: "Edit Block Property",
      icon: <Ban className="h-4 w-4" />,
      onBack: () => navigate("/property-availability/block"),
    });

    return () => setPageHeader(null); 
  }, [navigate, setPageHeader]);

  useEffect(() => {
    fetchProperties();
    fetchRoomTypes();
    fetchTourOperatorTypes();
  }, []);

  useEffect(() => {
    if (publicId && properties.length > 0) {
      loadBlockDetails(publicId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [publicId, properties]);

  const fetchProperties = async () => {
    try {
      setLoadingProperties(true);

      const response = await getProperties();

      const activeProperties = response.filter((property) => property.isActive);

      setProperties(activeProperties);
    } catch {
      toast.error("Failed to load properties");
    } finally {
      setLoadingProperties(false);
    }
  };

  const fetchTourOperatorTypes = async () => {
    try {
      setLoadingTourOperatorTypes(true);

      const response = await getTourOperatorTypes();

      const activeTypes = response.filter((type) => type.isActive);

      setTourOperatorTypes(activeTypes);
    } catch {
      toast.error("Failed to load TO types");
    } finally {
      setLoadingTourOperatorTypes(false);
    }
  };

  const fetchPropertyDetails = async (propertyName: string) => {
    try {
      setLoadingPropertyDetails(true);

      const selectedProperty = properties.find(
        (p) => p.propertyName === propertyName,
      );

      if (!selectedProperty) return;

      const response = await getPropertyById(selectedProperty.publicId);

      setSelectedPropertyDetails(response);

      setPropertyRoomTypes(response.roomTypes || []);

      setBlockFormData((prev) => ({
        ...prev,
        roomType: prev.roomType || response.roomTypes?.[0]?.roomTypeName || "",
      }));
    } catch {
      toast.error("Failed to load property details");
    } finally {
      setLoadingPropertyDetails(false);
    }
  };

  const fetchRoomTypes = async () => {
    try {
      const response = await getRoomTypes();

      const activeRoomTypes = response.filter((roomType) => roomType.isActive);

      setRoomTypes(activeRoomTypes);
    } catch {
      toast.error("Failed to load room types");
    }
  };

  const loadBlockDetails = async (publicId: string) => {
    try {
      setLoading(true);

      const response = await getPropertyAvailabilityBlockById(publicId);

      await fetchPropertyDetails(response.propertyName);
      setBlockFormData({
        propertyName: response.propertyName,
        roomType: response.roomTypeName,
        numberOfRooms: response.numberOfRooms,
        availableTo: response.availableTourTypeIds.map(String),
        blockStartDate: response.blockStartDate.split("T")[0],
        blockEndDate: response.blockEndDate.split("T")[0],
        remark: response.remark,
        status: response.isActive ? "active" : "inactive",
      });
    } catch {
      toast.error("Failed to load block details");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateBlockProperty = async () => {
    const newErrors = {
      propertyName: "",
      roomType: "",
      numberOfRooms: "",
      availableTo: "",
      blockStartDate: "",
      blockEndDate: "",
      remark: "",
    };

    if (!blockFormData.propertyName) {
      newErrors.propertyName = "Property name is required";
    }

    if (!blockFormData.roomType) {
      newErrors.roomType = "Room type is required";
    }

    if (blockFormData.availableTo.length === 0) {
      newErrors.availableTo = "Please select at least one TO type";
    }

    if (!blockFormData.blockStartDate) {
      newErrors.blockStartDate = "Please select start date";
    }

    if (!blockFormData.blockEndDate) {
      newErrors.blockEndDate = "Please select end date";
    }

    if (
      blockFormData.blockStartDate &&
      blockFormData.blockEndDate &&
      new Date(blockFormData.blockEndDate) <=
        new Date(blockFormData.blockStartDate)
    ) {
      newErrors.blockEndDate = "End date must be after start date";
    }

    if (!blockFormData.remark) {
      newErrors.remark = "Remark is required";
    }

    setErrors(newErrors);

    if (Object.values(newErrors).some((error) => error !== "")) {
      toast.error("Please fill all required fields");
      return;
    }

    try {
      setLoading(true);

      if (!selectedPropertyDetails) {
        toast.error("Property details not found");
        return;
      }

      const selectedRoomType = propertyRoomTypes.find(
        (rt) => rt.roomTypeName === blockFormData.roomType,
      );

      if (!selectedRoomType) {
        toast.error("Room type not found");
        return;
      }

      const payload = {
        propertyId: selectedPropertyDetails.propertyId,
        propertyRoomTypeId: selectedRoomType.propertyRoomTypeId,
        numberOfRooms: blockFormData.numberOfRooms,
        blockStartDate: new Date(blockFormData.blockStartDate).toISOString(),
        blockEndDate: new Date(blockFormData.blockEndDate).toISOString(),
        availableTourTypeIds: blockFormData.availableTo.map(Number),
        remark: blockFormData.remark,
        isActive: blockFormData.status === "active",
      };

      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      await updatePropertyAvailabilityBlock(publicId!, payload);

      toast.success("Blocked property updated successfully");

      navigate("/property-availability/block");
    } catch (error) {
      console.error(error);

      toast.error("Failed to update blocked property");
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {(loading ||
        loadingProperties ||
        loadingTourOperatorTypes ||
        loadingPropertyDetails) && <FullScreenLoading />}
      <div className="space-y-6">
        {/* Top Header */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 mb-4">
          <div>
            <p className="text-gray-600 mt-1 ml-0 sm:ml-2">
              Update blocked property room details and booking restrictions
            </p>
          </div>

          {/* Top Right Buttons */}
          <div className="flex justify-end gap-3">
            <Button
              variant="outline"
              onClick={() => navigate("/property-availability/block")}
            >
              Cancel
            </Button>

            <Button
              onClick={handleUpdateBlockProperty}
              style={{ background: "#5B9BD5" }}
              className="text-white"
            >
              <Ban className="w-4 h-4 mr-2" />
              Update Property
            </Button>
          </div>
        </div>

        {/* Main Card */}
        <Card
          className="bg-white border-[#5B9BD5]/30"
          style={{ width: "80%", margin: "auto" }}
        >
          <CardContent style={{ padding: "10px 16px 16px 16px" }}>
            <div className="grid gap-4">
              {/* Property Name */}
              <div className="space-y-2">
                <SelectFieldset label="Property Name" required>
                  <Select
                    value={blockFormData.propertyName}
                    disabled={true}
                    onValueChange={async (value) => {
                      setBlockFormData((prev) => ({
                        ...prev,
                        propertyName: value,
                        roomType: "",
                      }));

                      setErrors((prev) => ({
                        ...prev,
                        propertyName: "",
                      }));

                      await fetchPropertyDetails(value);
                    }}
                  >
                    <SelectTriggerUI>
                      <SelectValue
                        placeholder={
                          loadingProperties
                            ? "Loading properties..."
                            : "Select property"
                        }
                      />
                    </SelectTriggerUI>

                    <SelectContent>
                      {properties.map((property) => (
                        <SelectItem
                          key={property.publicId}
                          value={property.propertyName}
                        >
                          <div className="flex items-center gap-2">
                            <Building2 className="w-4 h-4 text-green-600" />
                            {property.propertyName}({property.propertyTypeName})
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </SelectFieldset>

                {errors.propertyName && (
                  <p className="text-red-500 text-xs">{errors.propertyName}</p>
                )}
              </div>

              {/* SHOW REMAINING FORM ONLY AFTER PROPERTY SELECT */}
              {blockFormData.propertyName && (
                <>
                  {/* Room Type + No Rooms */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <SelectFieldset label="Room Type" required>
                        <Select
                          value={blockFormData.roomType}
                          onValueChange={(value) => {
                            setBlockFormData({
                              ...blockFormData,
                              roomType: value,
                            });

                            setErrors({
                              ...errors,
                              roomType: "",
                            });
                          }}
                        >
                          <SelectTriggerUI>
                            <SelectValue
                              placeholder={
                                loadingPropertyDetails
                                  ? "Loading room types..."
                                  : "Select room type"
                              }
                            />
                          </SelectTriggerUI>

                          <SelectContent>
                            {roomTypes.map((roomType) => {
                              const isAllowed = propertyRoomTypes.some(
                                (prt) => prt.roomTypeName === roomType.roomName,
                              );

                              return (
                                <SelectItem
                                  key={roomType.publicId}
                                  value={roomType.roomName}
                                  disabled={!isAllowed}
                                  className={
                                    !isAllowed
                                      ? "opacity-50 cursor-not-allowed"
                                      : ""
                                  }
                                >
                                  {roomType.roomName}
                                </SelectItem>
                              );
                            })}
                          </SelectContent>
                        </Select>
                      </SelectFieldset>

                      {errors.roomType && (
                        <p className="text-red-500 text-xs">
                          {errors.roomType}
                        </p>
                      )}
                    </div>

                    <InputFieldset
                      label="Number of Rooms"
                      required
                      id="numberOfRooms"
                      type="number"
                      value={blockFormData.numberOfRooms}
                      onChange={(e) =>
                        setBlockFormData({
                          ...blockFormData,
                          numberOfRooms: parseInt(e.target.value) || 1,
                        })
                      }
                    />
                  </div>

                  {/* Dates */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <InputFieldset
                        label="Block Start Date"
                        required
                        id="blockStartDate"
                        type="date"
                        min={today}
                        value={blockFormData.blockStartDate}
                        onChange={(e) => {
                          const newStartDate = e.target.value;

                          setBlockFormData({
                            ...blockFormData,
                            blockStartDate: newStartDate,

                            blockEndDate:
                              blockFormData.blockEndDate &&
                              new Date(blockFormData.blockEndDate) <=
                                new Date(newStartDate)
                                ? ""
                                : blockFormData.blockEndDate,
                          });

                          setErrors({
                            ...errors,
                            blockStartDate: "",
                            blockEndDate: "",
                          });
                        }}
                      />

                      {errors.blockStartDate && (
                        <p className="text-red-500 text-xs">
                          {errors.blockStartDate}
                        </p>
                      )}
                    </div>

                    <div className="space-y-2">
                      <InputFieldset
                        label="Block End Date"
                        required
                        id="blockEndDate"
                        type="date"
                        min={blockFormData.blockStartDate || today}
                        value={blockFormData.blockEndDate}
                        onChange={(e) => {
                          setBlockFormData({
                            ...blockFormData,
                            blockEndDate: e.target.value,
                          });

                          setErrors({
                            ...errors,
                            blockEndDate: "",
                          });
                        }}
                      />
                      {errors.blockEndDate && (
                        <p className="text-red-500 text-xs">
                          {errors.blockEndDate}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {/* Available To */}
                    <div className="space-y-2">
                      <Label>Available to</Label>

                      <MultiSelect
                        options={tourOperatorTypes.map((type) => ({
                          value: String(type.tourOperatorTypeId),
                          label: type.typeName,
                        }))}
                        selected={blockFormData.availableTo}
                        onChange={(selected) => {
                          setBlockFormData({
                            ...blockFormData,
                            availableTo: selected,
                          });

                          setErrors({
                            ...errors,
                            availableTo: "",
                          });
                        }}
                        disabled={loadingTourOperatorTypes}
                        placeholder={
                          loadingTourOperatorTypes
                            ? "Loading TO types..."
                            : "Select TO types..."
                        }
                      />

                      {errors.availableTo && (
                        <p className="text-red-500 text-xs mt-1">
                          {errors.availableTo}
                        </p>
                      )}
                    </div>
                    <div className="space-y-2">
                      <SelectFieldset label="Status">
                        <Select
                          value={blockFormData.status}
                          onValueChange={(value: "active" | "inactive") =>
                            setBlockFormData({
                              ...blockFormData,
                              status: value,
                            })
                          }
                        >
                          <SelectTriggerUI className="h-9 text-sm">
                            <SelectValue />
                          </SelectTriggerUI>

                          <SelectContent>
                            <SelectItem value="active">Active</SelectItem>

                            <SelectItem value="inactive">Inactive</SelectItem>
                          </SelectContent>
                        </Select>
                      </SelectFieldset>
                    </div>
                  </div>

                  {/* Remark */}
                  <div className="space-y-2">
                    <SelectFieldset label="Remark" required>
                      <TextareaUI
                        placeholder="Enter remark"
                        value={blockFormData.remark}
                        onChange={(e) =>
                          setBlockFormData({
                            ...blockFormData,
                            remark: e.target.value,
                          })
                        }
                      />
                    </SelectFieldset>

                    {errors.remark && (
                      <p className="text-red-500 text-xs">{errors.remark}</p>
                    )}
                  </div>
                </>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
};

export default BlockPropertyEdit;
